/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ComponentType, type ReactElement } from 'react';
import { CreditCard, Facebook, Globe, Instagram, User } from 'lucide-react';
import { SiX } from '@icons-pack/react-simple-icons';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { PinterestIcon } from '@/components/icons';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { UITarget } from '@/targets/ui-target';

type UtilityLink = {
    labelKey: string;
    to: string;
    Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
};

type SocialLink = {
    href: string;
    labelKey: string;
    Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
    iconClassName?: string;
};

const socialLinks: SocialLink[] = [
    {
        href: 'https://www.facebook.com/AshleyHomeStore/',
        labelKey: 'socialMedia.facebookLabel',
        Icon: Facebook,
    },
    {
        href: 'https://www.instagram.com/ashleyhomestore/',
        labelKey: 'socialMedia.instagramLabel',
        Icon: Instagram,
    },
    {
        href: 'https://twitter.com/AshleyHomeStore',
        labelKey: 'socialMedia.xLabel',
        Icon: SiX,
    },
    {
        href: 'https://www.pinterest.com/ashleyhomestore/',
        labelKey: 'socialMedia.pinterestLabel',
        Icon: PinterestIcon,
    },
];

const utilityLinks: UtilityLink[] = [
    {
        labelKey: 'utilityLinks.financing',
        to: '/financing',
        Icon: CreditCard,
    },
    {
        labelKey: 'utilityLinks.login',
        to: '/login',
        Icon: User,
    },
    {
        labelKey: 'utilityLinks.chooseCountry',
        to: '/country-selector',
        Icon: Globe,
    },
];

interface UtilityLinksProps {
    className?: string;
}

const linkClassName =
    'inline-flex w-full items-center gap-2 rounded-sm py-1 text-sm leading-5 text-footer-foreground/80 underline-offset-4 transition-colors hover:text-footer-foreground hover:underline focus-visible:text-footer-foreground focus-visible:underline';

export default function UtilityLinks({ className }: UtilityLinksProps): ReactElement {
    const { t: tFooter } = useTranslation('footer');
    const t = tFooter as (key: string) => string;

    return (
        <div className={cn('flex w-full flex-col gap-4 lg:gap-6', className)}>
            <nav aria-label={t('socialMedia.label')} className="flex items-center gap-2">
                {socialLinks.map(({ href, labelKey, Icon, iconClassName }) => (
                    <Button
                        key={labelKey}
                        asChild
                        variant="ghost"
                        size="icon-sm"
                        className="size-8 rounded-full bg-footer-foreground p-0 text-footer-background hover:bg-footer-foreground/80 hover:text-footer-background focus-visible:ring-focus">
                        <a href={href} target="_blank" rel="noreferrer" aria-label={t(labelKey)}>
                            <Icon aria-hidden={true} className={cn('size-4', iconClassName)} />
                        </a>
                    </Button>
                ))}
            </nav>

            <nav aria-label={t('utilityLinks.label')}>
                <ul className="flex flex-col gap-2">
                    <UITarget targetId="sfcc.footer.ourcompany.start" />
                    {utilityLinks.map(({ labelKey, to, Icon }) => (
                        <li key={labelKey}>
                            <Link to={to} className={linkClassName}>
                                <Icon aria-hidden={true} className="size-4 shrink-0" />
                                <span>{t(labelKey)}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
        </div>
    );
}
